/* ============================================================
   EXTFOX - FAQ presentation
   Live search, category scroll-spy, deep links, expand-all,
   copy-link, helpfulness feedback, reveal-on-scroll.
   ============================================================ */

(function () {
  'use strict';

  var SCROLL_OFFSET = 110;

  // U+0300-U+036F: combining diacritics, stripped so "cafe" matches "café".
  var COMBINING_MARKS = new RegExp('[' + String.fromCharCode(768) + '-' + String.fromCharCode(879) + ']', 'g');

  function normalize(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(COMBINING_MARKS, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* ---------- Minimal JSON / Liquid / HTML tokeniser ----------
     The code samples are theme-editor fields, so everything is escaped
     before it is wrapped in a token span. */

  var TOKENS = /("(?:[^"\\]|\\.)*")(\s*:)?|(\{%[\s\S]*?%\}|\{\{[\s\S]*?\}\})|(<\/?[a-zA-Z][^>]*>)|([{}[\],:])|(\/\/[^\n]*|#[^\n]*)/g;

  function highlight(source) {
    var out = '';
    var last = 0;
    var match;

    TOKENS.lastIndex = 0;
    while ((match = TOKENS.exec(source)) !== null) {
      out += escapeHtml(source.slice(last, match.index));
      last = TOKENS.lastIndex;

      if (match[1]) {
        // A quoted string; a trailing colon makes it an object key.
        out +=
          '<span class="faqx__c-' +
          (match[2] ? 'key' : 'str') +
          '">' +
          escapeHtml(match[1]) +
          '</span>' +
          (match[2] ? '<span class="faqx__c-pun">' + escapeHtml(match[2]) + '</span>' : '');
      } else if (match[3] || match[4]) {
        out += '<span class="faqx__c-tag">' + escapeHtml(match[3] || match[4]) + '</span>';
      } else if (match[5]) {
        out += '<span class="faqx__c-pun">' + escapeHtml(match[5]) + '</span>';
      } else if (match[6]) {
        out += '<span class="faqx__c-com">' + escapeHtml(match[6]) + '</span>';
      }
    }

    return out + escapeHtml(source.slice(last));
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ============================================================
     Accordion
     A native <details> snaps open: the browser reveals the content
     in a single frame and no CSS transition can catch it. So the
     summary click is intercepted and the height of the <details>
     itself is animated with the Web Animations API, with `open`
     flipped only at the right end of the animation.
     ============================================================ */

  var EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var DURATION_OPEN = 420;
  var DURATION_CLOSE = 320;

  function Accordion(details) {
    this.el = details;
    this.summary = details.querySelector('summary');
    this.content = details.querySelector('[data-faqx-answer]');
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    if (!this.summary || !this.content || typeof details.animate !== 'function') return;

    this.enabled = true;

    var styles = window.getComputedStyle(details);
    this.borders = (parseFloat(styles.borderTopWidth) || 0) + (parseFloat(styles.borderBottomWidth) || 0);

    var self = this;
    this.summary.addEventListener('click', function (event) {
      event.preventDefault();
      self.toggle();
    });
  }

  Accordion.prototype.duration = function (opening) {
    if (prefersReducedMotion()) return 0;
    return opening ? DURATION_OPEN : DURATION_CLOSE;
  };

  Accordion.prototype.isOpen = function () {
    return this.el.open && !this.isClosing;
  };

  Accordion.prototype.toggle = function () {
    if (this.isClosing || !this.el.open) {
      this.expand();
    } else {
      this.shrink();
    }
  };

  Accordion.prototype.animateTo = function (endHeight, opening) {
    var self = this;
    var startHeight = this.el.offsetHeight;

    if (this.animation) this.animation.cancel();
    this.el.style.overflow = 'hidden';

    this.animation = this.el.animate(
      { height: [startHeight + 'px', endHeight + 'px'] },
      { duration: this.duration(opening), easing: EASE }
    );

    this.animation.onfinish = function () {
      self.settle(opening);
    };
    this.animation.oncancel = function () {
      self.isClosing = false;
      self.isExpanding = false;
    };
  };

  Accordion.prototype.expand = function () {
    if (!this.enabled) {
      this.el.open = true;
      return;
    }
    // Lock the collapsed height before `open` reflows the content in.
    this.el.style.height = this.el.offsetHeight + 'px';
    this.el.style.overflow = 'hidden';
    this.el.open = true;
    this.isExpanding = true;
    this.isClosing = false;

    var self = this;
    window.requestAnimationFrame(function () {
      self.animateTo(self.summary.offsetHeight + self.content.offsetHeight + self.borders, true);
    });
  };

  Accordion.prototype.shrink = function () {
    if (!this.enabled) {
      this.el.open = false;
      return;
    }
    this.isClosing = true;
    this.isExpanding = false;
    this.animateTo(this.summary.offsetHeight + this.borders, false);
  };

  Accordion.prototype.settle = function (open) {
    this.el.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.el.style.height = '';
    this.el.style.overflow = '';
  };

  Accordion.prototype.setOpen = function (shouldOpen) {
    if (shouldOpen === this.isOpen()) return;
    if (shouldOpen) {
      this.expand();
    } else {
      this.shrink();
    }
  };

  // Used when filtering hides an item mid-animation - no transition wanted.
  Accordion.prototype.reset = function (open) {
    if (this.animation) this.animation.cancel();
    this.settle(open);
  };

  function FaqPresentation(root) {
    this.root = root;
    this.groups = Array.prototype.slice.call(root.querySelectorAll('[data-faqx-group]'));
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-faqx-item]'));
    this.railLinks = Array.prototype.slice.call(root.querySelectorAll('[data-faqx-rail-link]'));
    this.search = root.querySelector('[data-faqx-search]');
    this.searchWrap = root.querySelector('[data-faqx-search-wrap]');
    this.searchClears = Array.prototype.slice.call(root.querySelectorAll('[data-faqx-search-clear]'));
    this.count = root.querySelector('[data-faqx-count]');
    this.countSuffix = this.count ? this.count.getAttribute('data-faqx-count') || '' : '';
    this.empty = root.querySelector('[data-faqx-empty]');
    this.toggleAll = root.querySelector('[data-faqx-toggle-all]');
    this.status = root.querySelector('[data-faqx-status]');

    this.total = this.items.length;
    this.spyTicking = false;
    this.railLocked = 0;

    this.items.forEach(function (item) {
      var question = item.querySelector('[data-faqx-question]');
      if (question) item._faqxQuestion = question.textContent;
      item._faqxHaystack = normalize(item.getAttribute('data-search'));
      item._faqxAccordion = new Accordion(item);
    });

    this.bindSearch();
    this.bindRail();
    this.bindToggleAll();
    this.bindItems();
    this.bindShortcuts();
    this.bindDevPanel();
    this.bindLayouts();
    this.bindLang();
    this.observeReveal();
    this.bindSpy();
    this.openFromHash();
  }

  /* ---------- Search ---------- */

  FaqPresentation.prototype.bindSearch = function () {
    var self = this;
    if (!this.search) return;

    this.search.addEventListener('input', function () {
      self.applyFilter(self.search.value);
    });

    this.search.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.search.value !== '') {
        event.stopPropagation();
        self.resetSearch();
      }
    });

    // Never submit - filtering is instant.
    var form = this.search.closest('form');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
      });
    }

    this.searchClears.forEach(function (button) {
      button.addEventListener('click', function () {
        self.resetSearch();
      });
    });

    Array.prototype.slice.call(this.root.querySelectorAll('[data-faqx-chip]')).forEach(function (chip) {
      chip.addEventListener('click', function () {
        var targetId = chip.getAttribute('data-faqx-chip');
        var target = targetId ? document.getElementById(targetId) : null;
        if (!target) return;
        self.resetSearch(true);
        self.revealItem(target);
      });
    });
  };

  FaqPresentation.prototype.resetSearch = function (silent) {
    if (this.search) this.search.value = '';
    this.applyFilter('', silent);
    if (!silent && this.search) this.search.focus();
  };

  FaqPresentation.prototype.applyFilter = function (rawQuery, silent) {
    var query = normalize(rawQuery);
    var tokens = query === '' ? [] : query.split(' ');
    var visible = 0;

    if (this.searchWrap) this.searchWrap.classList.toggle('is-filled', rawQuery !== '');

    this.items.forEach(function (item) {
      var match =
        tokens.length === 0 ||
        tokens.every(function (token) {
          return item._faqxHaystack.indexOf(token) !== -1;
        });

      item.hidden = !match;
      if (match) {
        visible += 1;
      } else if (item.open) {
        item._faqxAccordion.reset(false);
      }
    });

    this.highlight(tokens);

    this.groups.forEach(function (group) {
      var hasVisible = !!group.querySelector('[data-faqx-item]:not([hidden])');
      group.hidden = !hasVisible;
    });

    this.railLinks.forEach(function (link) {
      var group = document.getElementById(link.getAttribute('data-faqx-rail-link'));
      link.hidden = !!(group && group.hidden);
    });

    if (this.empty) this.empty.hidden = visible !== 0;
    this.updateCount(visible, tokens.length > 0);

    if (!silent && this.status && tokens.length > 0) {
      this.status.textContent = visible + (visible === 1 ? ' answer matches' : ' answers match') + ' your search.';
    } else if (this.status) {
      this.status.textContent = '';
    }

    if (this.toggleAll) {
      this.toggleAll.classList.remove('is-open');
      this.setToggleLabel(false);
    }
  };

  FaqPresentation.prototype.highlight = function (tokens) {
    this.items.forEach(function (item) {
      var node = item.querySelector('[data-faqx-question]');
      if (!node || typeof item._faqxQuestion !== 'string') return;

      var original = item._faqxQuestion;
      if (!tokens.length || item.hidden) {
        if (node.textContent !== original) node.textContent = original;
        return;
      }

      var pattern = tokens
        .filter(function (token) {
          return token.length > 1;
        })
        .map(escapeRegExp)
        .join('|');

      if (!pattern) {
        node.textContent = original;
        return;
      }

      node.innerHTML = escapeHtml(original).replace(new RegExp('(' + pattern + ')', 'gi'), '<mark>$1</mark>');
    });
  };

  FaqPresentation.prototype.updateCount = function (visible, filtering) {
    if (!this.count) return;
    if (filtering) {
      this.count.textContent = '';
      this.count.appendChild(document.createElement('b')).textContent = String(visible);
      this.count.appendChild(document.createTextNode(' of ' + this.total + ' answers'));
    } else {
      this.count.textContent = '';
      this.count.appendChild(document.createElement('b')).textContent = String(this.total);
      this.count.appendChild(document.createTextNode(' ' + this.countSuffix));
    }
  };

  /* ---------- Category rail ---------- */

  FaqPresentation.prototype.bindRail = function () {
    var self = this;
    this.railLinks.forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        var group = document.getElementById(link.getAttribute('data-faqx-rail-link'));
        if (!group) return;

        self.setActiveRail(link);
        // Hold scroll-spy briefly so the clicked link stays highlighted.
        self.railLocked = Date.now() + 700;
        group.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (history.replaceState) history.replaceState(null, '', '#' + group.id);
      });
    });
  };

  FaqPresentation.prototype.setActiveRail = function (activeLink) {
    this.railLinks.forEach(function (link) {
      link.classList.toggle('is-active', link === activeLink);
      if (link === activeLink) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  FaqPresentation.prototype.bindSpy = function () {
    var self = this;
    var onScroll = function () {
      if (self.spyTicking) return;
      self.spyTicking = true;
      window.requestAnimationFrame(function () {
        self.spyTicking = false;
        self.updateSpy();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    this.updateSpy();
  };

  FaqPresentation.prototype.updateSpy = function () {
    if (Date.now() < this.railLocked) return;

    var current = null;
    this.groups.forEach(function (group) {
      if (group.hidden) return;
      if (group.getBoundingClientRect().top - SCROLL_OFFSET <= 0) current = group;
    });

    if (!current) {
      current = this.groups.filter(function (group) {
        return !group.hidden;
      })[0];
    }
    if (!current) return;

    var link = this.railLinks.filter(function (item) {
      return item.getAttribute('data-faqx-rail-link') === current.id;
    })[0];

    if (link) this.setActiveRail(link);
  };

  /* ---------- Expand / collapse all ---------- */

  FaqPresentation.prototype.setToggleLabel = function (isOpen) {
    if (!this.toggleAll) return;
    var label = this.toggleAll.querySelector('[data-faqx-toggle-label]');
    if (label) label.textContent = isOpen ? 'Collapse all' : 'Expand all';
    this.toggleAll.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  };

  FaqPresentation.prototype.bindToggleAll = function () {
    var self = this;
    if (!this.toggleAll) return;

    this.toggleAll.addEventListener('click', function () {
      var opening = !self.toggleAll.classList.contains('is-open');
      self.items.forEach(function (item) {
        if (!item.hidden) item._faqxAccordion.setOpen(opening);
      });
      self.toggleAll.classList.toggle('is-open', opening);
      self.setToggleLabel(opening);
    });
  };

  /* ---------- Items: copy link + helpfulness ---------- */

  FaqPresentation.prototype.bindItems = function () {
    var self = this;

    this.items.forEach(function (item) {
      var copy = item.querySelector('[data-faqx-copy]');
      if (copy) {
        copy.addEventListener('click', function (event) {
          event.preventDefault();
          var url = window.location.origin + window.location.pathname + '#' + item.id;
          self.copyToClipboard(url);

          var label = copy.querySelector('[data-faqx-copy-label]');
          var previous = label ? label.textContent : '';
          copy.classList.add('is-copied');
          if (label) label.textContent = 'Link copied';
          window.setTimeout(function () {
            copy.classList.remove('is-copied');
            if (label) label.textContent = previous;
          }, 2000);
        });
      }

      Array.prototype.slice.call(item.querySelectorAll('[data-faqx-vote]')).forEach(function (vote) {
        vote.addEventListener('click', function (event) {
          event.preventDefault();
          var foot = vote.closest('[data-faqx-foot]');
          if (foot) foot.classList.add('is-voted');
        });
      });
    });
  };

  FaqPresentation.prototype.copyToClipboard = function (text) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(function () {
        /* clipboard blocked - the visual confirmation is harmless */
      });
      return;
    }
    var helper = document.createElement('textarea');
    helper.value = text;
    helper.setAttribute('readonly', '');
    helper.style.position = 'absolute';
    helper.style.left = '-9999px';
    document.body.appendChild(helper);
    helper.select();
    try {
      document.execCommand('copy');
    } catch (error) {
      /* no-op */
    }
    document.body.removeChild(helper);
  };

  /* ---------- Deep links ---------- */

  FaqPresentation.prototype.openFromHash = function () {
    var hash = window.location.hash.replace('#', '');
    if (!hash) return;
    var target = document.getElementById(hash);
    if (!target) return;

    if (target.hasAttribute('data-faqx-item')) {
      this.revealItem(target, true);
    } else if (target.hasAttribute('data-faqx-group')) {
      this.railLocked = Date.now() + 700;
      target.scrollIntoView({ block: 'start' });
    }
  };

  FaqPresentation.prototype.revealItem = function (item, instant) {
    if (instant) {
      item._faqxAccordion.reset(true);
    } else {
      item._faqxAccordion.setOpen(true);
    }
    item.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'start' });
    item.classList.add('is-target');
    window.setTimeout(function () {
      item.classList.remove('is-target');
    }, 1700);
  };

  /* ---------- Keyboard shortcuts ---------- */

  FaqPresentation.prototype.bindShortcuts = function () {
    var self = this;
    if (!this.search) return;

    document.addEventListener('keydown', function (event) {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;

      var active = document.activeElement;
      var tag = active ? active.tagName : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (active && active.isContentEditable)) return;

      event.preventDefault();
      self.search.focus();
      self.search.select();
    });
  };

  /* ---------- Developer panel ---------- */

  FaqPresentation.prototype.bindDevPanel = function () {
    var self = this;

    Array.prototype.slice.call(this.root.querySelectorAll('[data-faqx-code]')).forEach(function (node) {
      node.innerHTML = highlight(node.textContent);
    });

    var tabs = Array.prototype.slice.call(this.root.querySelectorAll('[data-faqx-dev-tab]'));
    if (!tabs.length) return;

    var select = function (tab, focus) {
      tabs.forEach(function (other) {
        var active = other === tab;
        var pane = document.getElementById(other.getAttribute('data-faqx-dev-tab'));

        other.classList.toggle('is-active', active);
        other.setAttribute('aria-selected', active ? 'true' : 'false');
        other.tabIndex = active ? 0 : -1;
        if (pane) pane.hidden = !active;
      });
      if (focus) tab.focus();
    };

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        select(tab);
      });

      tab.addEventListener('keydown', function (event) {
        var target = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          target = tabs[(index + 1) % tabs.length];
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          target = tabs[(index - 1 + tabs.length) % tabs.length];
        } else if (event.key === 'Home') {
          target = tabs[0];
        } else if (event.key === 'End') {
          target = tabs[tabs.length - 1];
        }

        if (!target) return;
        event.preventDefault();
        select(target, true);
      });
    });

    self.devTabs = tabs;
  };

  /* ---------- Layouts gallery lightbox ---------- */

  FaqPresentation.prototype.bindLayouts = function () {
    var gallery = this.root.querySelector('[data-faqx-gallery]');
    if (!gallery) return;

    Array.prototype.slice.call(gallery.querySelectorAll('[data-faqx-view]')).forEach(function (button) {
      button.addEventListener('click', function () {
        var dialog = document.getElementById(button.getAttribute('data-faqx-view'));
        if (!dialog) return;
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
      });
    });

    Array.prototype.slice.call(gallery.querySelectorAll('[data-faqx-lightbox]')).forEach(function (dialog) {
      var close = dialog.querySelector('[data-faqx-close]');
      if (close) {
        close.addEventListener('click', function () {
          dialog.close ? dialog.close() : dialog.removeAttribute('open');
        });
      }
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) {
          dialog.close ? dialog.close() : dialog.removeAttribute('open');
        }
      });
      dialog.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && dialog.open) {
          event.stopPropagation();
          dialog.close();
        }
      });
    });
  };

  /* ---------- Language minis: working accordions ---------- */

  FaqPresentation.prototype.bindLang = function () {
    var wrap = this.root.querySelector('[data-faqx-lang]');
    if (!wrap) return;

    Array.prototype.slice.call(wrap.querySelectorAll('[data-faqx-lang-item]')).forEach(function (details) {
      if (details._faqxMiniReady) return;
      details._faqxMiniReady = true;
      new Accordion(details);
    });
  };

  /* ---------- Reveal on scroll ---------- */

  FaqPresentation.prototype.observeReveal = function () {
    var targets = Array.prototype.slice.call(this.root.querySelectorAll('.faqx__reveal'));
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (target) {
        target.classList.add('is-in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach(function (target, index) {
      target.style.transitionDelay = Math.min(index, 6) * 60 + 'ms';
      observer.observe(target);
    });
  };

  /* ---------- Boot ---------- */

  function init(scope) {
    Array.prototype.slice.call((scope || document).querySelectorAll('[data-faqx]')).forEach(function (root) {
      if (root._faqxReady) return;
      root._faqxReady = true;
      new FaqPresentation(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
